import PropTypes from 'prop-types';
import { css } from 'styled-components';
import colors from '../../themes/Colors';

/**
 * Adds a circular ripple effect to an element.
 *
 * @param {number} speed speed of ripple effect
 * @param {number} opacity opacity to end in
 * @param {string} color the color of the ripple in valid hexadecimal format
 *
 * @returns {string} css literal
 */
function Ripple(speed = 0.5, opacity = 1, color = colors.grayscale.white) {
  const spd = typeof speed !== 'number' ? 0.5 : speed;
  const opc =
    typeof opacity !== 'number' || opacity > 1 || opacity < 0 ? 1 : opacity;
  const clr =
    typeof color === 'string' && color.match(/^#[0-9A-F]{6}$/)
      ? color
      : colors.grayscale.white;

  return css`
    overflow: hidden;
    position: relative;
    transform: translate3d(0, 0, 0);

    &::after {
      background-image: radial-gradient(
        circle at center,
        ${clr} 10%,
        transparent 1%
      );
      background-position: 50%;
      background-repeat: no-repeat;
      content: '';
      display: block;
      height: 100%;
      left: 0;
      opacity: 0;
      pointer-events: none;
      position: absolute;
      top: 0;
      transform: scale(10, 10);
      transition:
        transform ${spd}s,
        opacity ${opc}s;
      width: 100%;
    }

    &:active:enabled {
      &::after {
        opacity: 0.3;
        transition: 0s;
        transform: scale(0, 0);
      }
    }
  `;
}

/**
 * This is not a component with actual props, but using propTypes here we can
 * document the params of the function.  This is a workaround.  We'll work on
 * adding proper jsdoc documentation in another iteration.
 */
Ripple.propTypes = {
  /** Speed: The speed with which the ripple should animate */
  speed: PropTypes.number,
  /** The opacity to start at */
  opacity: PropTypes.number,
  /** The color of the ripple:
   *  Must be a valid hexadecimal not shorthand (E.g. #FFFFFF)
   *  If you use shorthand or 'red' it will fallback to the default.
   */
  color: PropTypes.string,
};

Ripple.defaultProps = {
  speed: 0.5,
  opacity: 1,
  color: colors.grayscale.white,
};

/* @component */
export default Ripple;
